import { SectionShell } from "@/components/design-system/SectionShell";
import { ColorSwatch } from "@/components/design-system/ColorSwatch";
import { SURFACES, TEXT, SPLASH, SANCTUARY_BRAND, LOGO_COLORS } from "@/components/design-system/palette";

const code = `<div className="bg-page text-ink-body">
  <div className="bg-surface border border-hairline">
    <h2 className="text-ink-display">Headline</h2>
    <p className="text-ink-muted">Quiet supporting copy.</p>
    <button className="bg-accent text-page">Primary action</button>
  </div>
</div>`;

export function ColorsSection() {
  return (
    <SectionShell
      id="colors"
      title="Colors"
      description={
        <>
          Semantic tokens mapped to the Sanctuary palette, each defined for light and dark mode. Use the
          token names — never raw hex. Each swatch shows the value for the
          mode you're currently viewing; both hex codes are listed below the
          swatch and click-to-copy. Raw Sanctuary brand tokens (teal, sage, stone, cream) are also available for direct use.
        </>
      }
      whenToUse={
        <ul>
          <li>Always reach for a token first.</li>
          <li>
            Use <code>page</code> / <code>surface</code> for layered
            backgrounds.
          </li>
          <li>
            Use <code>ink-display</code> for headlines and emphasized
            content.
          </li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>
            Never use raw hex values (<code>#1f2937</code>) in components.
          </li>
          <li>
            Don't introduce new color names ad-hoc — extend the system
            instead.
          </li>
        </ul>
      }
      preview={
        <div className="space-y-8">
          <div>
            <h3 className="mb-3 text-sm font-semibold text-ink-display">
              Surfaces
            </h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {SURFACES.map((c) => (
                <ColorSwatch key={c.name} {...c} />
              ))}
            </div>
          </div>
          <div>
            <h3 className="mb-3 text-sm font-semibold text-ink-display">
              Text
            </h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {TEXT.map((c) => (
                <ColorSwatch key={c.name} {...c} />
              ))}
            </div>
          </div>
          <div>
            <h3 className="mb-3 text-sm font-semibold text-ink-display">
              Splash
            </h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {SPLASH.map((c) => (
                <ColorSwatch key={c.name} {...c} />
              ))}
            </div>
          </div>
          <div>
            <h3 className="mb-3 text-sm font-semibold text-ink-display">
              Sanctuary brand palette
            </h3>
            <p className="mb-3 text-xs text-ink-muted">
              Raw brand tokens — teal, sage, stone, and cream. Use these when you need to reference a specific Sanctuary colour directly. Prefer semantic tokens for general UI.
            </p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {SANCTUARY_BRAND.map((c) => (
                <ColorSwatch key={c.name} {...c} />
              ))}
            </div>
          </div>
          <div>
            <h3 className="mb-3 text-sm font-semibold text-ink-display">
              Brand logo colors
            </h3>
            <p className="mb-3 text-xs text-ink-muted">
              The logo file's own colors — brighter and more saturated than the site's softened navy above (full audit: <code>docs/brand-colours.md</code>). <code>logo-navy-dark</code> is a second, darker option pulled from the Room Booking System login screenshot (a different app) rather than the main logo file. Reference only, for recreating the literal mark (an inline SVG, a favicon) — use the Sanctuary brand palette above for everything else.
            </p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {LOGO_COLORS.map((c) => (
                <ColorSwatch key={c.name} {...c} />
              ))}
            </div>
          </div>
        </div>
      }
      code={code}
    />
  );
}
